import PluginUtils, { PluginType } from '../../PluginUtils';
import { calcPriceObject, MULTIPLE_TICKETS_DETAILS_FIELD_NAME, type PriceData } from './engine';
import { getDepositInfo } from './deposit';
import { type PriceLine, PriceLineType, type PriceQuote, type PriceQuoteProps } from './types';
import { OrderPageDataTypes } from '../../../api';

const PRICE_LINE_TYPES: Record<string, PriceLineType> = {
  number: PriceLineType.QUANTITY,
  enum: PriceLineType.OPTION,
  bool: PriceLineType.TOGGLE,
  gt: PriceLineType.THRESHOLD,
  multiple_tickets_price: PriceLineType.TICKETS,
};

/** Price objects nest (a bool's price can be a number). The outer type is what the customer chose. */
const typeOfOutermost = (priceData: PriceData): PriceLineType => {
  const lineType = PRICE_LINE_TYPES[priceData.type ?? ''];
  if (!lineType) {
    // Unreachable: calcPriceObject loops forever on an unknown type before we get here.
    throw new Error(`Unknown price object type: ${priceData.type}`);
  }
  return lineType;
};

const orderDetailLabelOf = (priceData: PriceData): string => {
  // A multi-ticket price is data_type 'custom' with no name, so use the key it actually reads.
  if (priceData.type === 'multiple_tickets_price') {
    return MULTIPLE_TICKETS_DETAILS_FIELD_NAME;
  }
  return (priceData.data_type === OrderPageDataTypes.CUSTOM && priceData.name) || priceData.data_type;
};

export const quotePrice = ({
  orderPage,
  details,
  dateDetails,
  account,
  forceWeekendAddition,
}: PriceQuoteProps): PriceQuote => {
  const lines: PriceLine[] = [];
  let subtotal = 0;

  for (const priceDataItem of orderPage.price) {
    // calcPriceObject first, so a malformed price object behaves exactly as it did before.
    const amount = calcPriceObject(priceDataItem, details, orderPage);
    lines.push({
      orderDetailLabel: orderDetailLabelOf(priceDataItem),
      amount,
      type: typeOfOutermost(priceDataItem),
    });
    subtotal += amount;
  }

  // The plugin multiplies the summed total rather than any one price object, so it gets its own line.
  const total = PluginUtils.handleWeekendAndIsraeliHolidaysPlugin(
    orderPage.plugins,
    subtotal,
    dateDetails?.date,
    forceWeekendAddition,
    account?.holiday_calendar,
  );
  if (total !== subtotal) {
    lines.push({
      orderDetailLabel: PluginType.WEEKEND_AND_ISRAELI_HOLIDAYS,
      amount: total - subtotal,
      type: PriceLineType.SURCHARGE,
    });
  }

  return {
    lines,
    total,
    deposit: account ? getDepositInfo({ widgetOrderPageData: orderPage, details, dateDetails, account }) : null,
  };
};
